import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { GENDERS, chartKindFor, chartFor } from "@/config/sizeCharts";

// A size-chart modal. `category` decides footwear vs apparel; `defaultGender`
// seeds the Men/Women/Kids tab (e.g. from a product's department).
export default function SizeGuide({ open, onClose, category, defaultGender = "men" }) {
  const kind = chartKindFor(category);
  const [gender, setGender] = useState(defaultGender);
  useEffect(() => { if (open) setGender(defaultGender); }, [open, defaultGender]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !kind) return null;
  const chart = chartFor(category, gender);

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-background border border-foreground w-full sm:max-w-xl max-h-[85vh] overflow-y-auto">
        <div className="flex items-start justify-between px-6 pt-6">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Size guide</p>
            <h2 className="font-serif text-3xl tracking-tighter mt-1">{kind === "footwear" ? "Find your shoe size." : "Find your size."}</h2>
          </div>
          <button onClick={onClose} aria-label="Close size guide" className="hover:text-[hsl(var(--accent))] mt-1"><X size={22} strokeWidth={1.5} /></button>
        </div>

        {/* Gender tabs */}
        <div className="flex gap-2 px-6 mt-5">
          {GENDERS.map((g) => (
            <button key={g.v} onClick={() => setGender(g.v)}
              className={`px-4 py-2 font-mono text-[10px] uppercase tracking-widest border transition-colors ${gender === g.v ? "bg-foreground text-background border-foreground" : "border-foreground/30 hover:border-foreground"}`}>
              {g.label}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="px-6 mt-5 overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-foreground/30">
                {chart.columns.map((c) => (
                  <th key={c} className="text-left font-mono text-[9px] uppercase tracking-widest text-muted-foreground py-2 pr-4 whitespace-nowrap">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {chart.rows.map((row, i) => (
                <tr key={i} className="border-b border-foreground/10">
                  {row.map((cell, j) => (
                    <td key={j} className={`py-2.5 pr-4 whitespace-nowrap ${j === 0 ? "font-mono" : "text-muted-foreground"}`}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-6 py-5 mt-1">
          {kind === "footwear" ? (
            <p className="text-xs text-muted-foreground leading-relaxed">
              <span className="text-foreground">How to measure:</span> stand on a sheet of paper, mark heel and longest toe, measure the distance in cm, and match it to the <span className="text-foreground">Foot length</span> column. If you're between sizes, size up.
            </p>
          ) : (
            <p className="text-xs text-muted-foreground leading-relaxed">
              Measure around the fullest part of your chest/bust and your natural waist, keeping the tape level. If you're between sizes, size up for a relaxed fit.
            </p>
          )}
          <p className="mt-2 text-[11px] font-mono text-muted-foreground/70">Approximate reference · fit varies by brand</p>
        </div>
      </div>
    </div>
  );
}
